import constants from '../constants';
import * as helpers from '../../helpers';
import { DuplicatedItemInSpreadConfig } from '../html';
import { LinkType } from '../../../constants';

export enum ACTION_TYPE {
	releaseactions = 'releaseactions',
	clickactions = 'clickactions',
	rolloveractions = 'rolloveractions',
	rolloffactions = 'rolloffactions',
}

export interface ButtonConfig {
	id: string;
	page: number;
	isVisible: boolean;
	isThemedItem: boolean;
	hasLink: boolean;
	linkData: helpers.LinkData;
	shouldCorrect: boolean;
	correctedLink?: string;
	className: string;
	actionType?: ACTION_TYPE;
	inDesignAction?: string; // action which can't be edited like play animation or change MSO state
	isLocked?: boolean;
	isEditedFromBackoffice?: boolean;
}

interface ButtonsData {
	items: ButtonConfig[];
	duplicatedItemsInSpread: DuplicatedItemInSpreadConfig[];
	isError: boolean;
}

export enum ButtonElementDataAttribute {
	hidden = 'data-hidden', // is set when button is hidden
	hasLink = 'data-has-link', // is set when button has a link set in inDesign (need to keep it in case a link was removed from editor)
}

const getConfig = (
	element: HTMLDivElement,
	page: number,
	isAccountManagerOrAdmin: boolean,
	totalPages: number,
	spreads: number[][],
): ButtonConfig | undefined => {
	const filteredClassName = element.className.split(' ').find(className => (
		className.includes(constants.editableButtonClassName)
	)) || '';

	const isLocked = element.className.includes(constants.lockedClassName) ||
		helpers.hasLockedParent(element);
	const actionAttr =
		element.dataset[ACTION_TYPE.releaseactions] ||
		element.dataset[ACTION_TYPE.clickactions] ||
		element.dataset[ACTION_TYPE.rolloveractions] ||
		element.dataset[ACTION_TYPE.rolloffactions] ||
		'';
	const regExp = /goToDestination\(['"`](.+)['"`]\);/;
	const match = actionAttr.match(regExp);
	const action = match ? match[1] : '';
	const inDesignAction = match ? undefined : actionAttr;

	// locked buttons with inDesign action should be rendered as <ButtonEditor> to have ability to run its action
	// otherwise it won't be possible to change MSO state or open/close hidden blocks
	if (isLocked && !inDesignAction) {
		return;
	}

	const linkData = helpers.getButtonLinkParamsFromAction(action, totalPages, spreads);
	const shouldCorrect = helpers.shouldCorrectLink(linkData);

	return {
		page,
		id: element.id,
		className: filteredClassName,
		isVisible: !element.getAttribute(ButtonElementDataAttribute.hidden),
		isThemedItem: isAccountManagerOrAdmin && !!element.querySelector(`[class*=${constants.themedItemClassName}]`),
		hasLink: hasLink(element, linkData),
		linkData,
		actionType: getActionType(element),
		inDesignAction,
		isLocked,
		shouldCorrect,
		isEditedFromBackoffice: shouldCorrect,
	};
};

const hasLink = (element: HTMLDivElement, linkData: helpers.LinkData) => {
	if (element.getAttribute(ButtonElementDataAttribute.hasLink)) {
		return true;
	}

	if (linkData.type === LinkType.url && !linkData.data.href) {
		return false;
	}

	return true;
};

const getActionType = (element: HTMLDivElement): ACTION_TYPE | undefined => {
	const actionNames = Object.keys(element.dataset);

	if (actionNames.length === 0) {
		return;
	}

	return actionNames.includes(ACTION_TYPE.clickactions)
		? ACTION_TYPE.clickactions
		: ACTION_TYPE.releaseactions;
};

export const removeButtonActions = (htmlDocument: Document): Document => {
	const buttons = [
		...htmlDocument.querySelectorAll(`[class*=${constants.buttonClassName}]`),
	] as HTMLButtonElement[];

	buttons.forEach((button) => {
		for (const actionType in ACTION_TYPE) {
			button.removeAttribute(`data-${actionType}`);
		}
	});

	return htmlDocument;
};

export default (
	page: number,
	pageIndexInSpread: number,
	htmlDocument: Document,
	isAccountManagerOrAdmin: boolean,
	totalPages: number,
	spreads: number[][],
): ButtonsData => {
	const items: ButtonConfig[] = [];
	const duplicatedItemsInSpread: DuplicatedItemInSpreadConfig[] = [];
	let isError: boolean = false;

	const buttonElements = [
		...htmlDocument.querySelectorAll(`[class*=${constants.buttonClassName}]`),
	] as HTMLDivElement[];

	try {
		buttonElements.forEach((element) => {
			const config = getConfig(element, page, isAccountManagerOrAdmin, totalPages, spreads);

			if (config) {
				items.push(config);

				if (helpers.isMarkedForAdjacentPage(config.className, pageIndexInSpread)) {
					duplicatedItemsInSpread.push({ page, id: config.id, className: config.className });
				}
			}
		});
	} catch (err) {
		isError = true;
	}

	return {
		items,
		duplicatedItemsInSpread,
		isError,
	};
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/Editor/utils/indesignEditor/editableButtons/getData.ts